<template>
  <div>
    <vx-card title="" :is-filter="true" class="mb-8" :init-collapsed="true" :collapse-action="true" @keyup.enter="loadUsersList(1, null, true)">

      <template slot="actions">
        <vs-button color="primary" type="filled" icon-pack="feather" icon="icon-plus" @click="addNewUserPopup">{{ $t('add') }}</vs-button>
      </template>

      <div class="vx-row">
        <div class="vx-col md:w-1/1 sm:w-1/1 w-full">
          <vs-input :placeholder="$t('name')" v-model="usersSearchParams.search" class="w-full"/>
        </div>
      </div>

      <template slot="footer">
        <vs-button color="success" type="filled" class="w-full" @click.prevent="loadUsersList(1, null, true)" v-text="$t('search')"></vs-button>
      </template>

    </vx-card>

    <vx-card id="usersDataContainer" :title="$t('manage_users')" class="mb-8 vs-con-loading__container">

      <shared-user-list
        :users="users"
        :unusedExtensionOptions="unusedExtensionOptions"
        :departmentOptions="departmentOptions"
        @editUserPermissionPopup="editUserPermissionPopup"
        @badgeChangeStatus="badgeChangeStatus"
        @inlineUpdateUser="inlineUpdateUser"
        @inlineEditUserCancel="inlineEditUserCancel"
        @inlineEditUser="inlineEditUser"
        @deleteUser="deleteUser"
      ></shared-user-list>

      <template slot="footer">
        <paginate
          :total="userPages"
          :itemPerPage="itemPerPage"
          :click-handler="loadUsersList"
          v-model="initialPage"
          goto
        >
        </paginate>
      </template>
    </vx-card>

    <modal :title="$t('add_user')" :active.sync="modals.add" size="lg">
      <shared-add-user
        @registerUserSuccess="registerSuccess"
        :departments="departmentOptions"
        :unused-extensions="unusedExtensionOptions"
      ></shared-add-user>

      <vs-divider></vs-divider>

      <div class="flex justify-end">
        <vs-button id="addUserButton" color="primary" type="filled" @click.prevent="initRegister">{{ $t('register') }}</vs-button>
      </div>
    </modal>

    <modal :title="$t('edit_user_permission')" :active.sync="modals.permissions" size="lg">
      <div class="p-3">
        <shared-edit-user-permission @updateUserPermissionSuccess="updateUserPermissionSuccess"></shared-edit-user-permission>
      </div>

      <vs-divider></vs-divider>

      <div class="flex justify-end">
        <vs-button id="editUserPermissionButton" color="primary" type="filled" @click.prevent="updateUserPermission">{{ $t('save') }}</vs-button>
      </div>
    </modal>
  </div>
</template>

<script>
import User from '@/models/User'
import Paginate from '@/mixins/paginate'
import Pagination from '@/components/Paginate'
import Modal from '@/components/Modal'
import SharedUserList from '@/views/Shared/User/List'
import SharedAddUser from '@/views/Shared/User/Add'
import SharedEditUserPermission from '@/views/Shared/User/Permission/Edit'
import Utils from '@/helpers/utils'
import { EventBus } from '@/../eventbus'

export default {
  name: 'User.Users',
  mixins: [Paginate],
  components: {
    Pagination,
    SharedUserList,
    SharedAddUser,
    SharedEditUserPermission,
    Modal
  },
  data () {
    return {
      users: {},
      usersEdit: {},
      userPages: 1,
      departmentOptions: [],
      extensionOptions: [],
      unusedExtensionOptions: [],
      itemPerPage: null,
      statusObj: {
        0: 'In-active',
        1: 'Active'
      },
      usersSearchParams: {
        search: typeof this.$route.query.search !== 'undefined' ? this.$route.query.search : ''
      },
      modals: {
        add: false,
        permissions: false
      }
    }
  },
  watch: {
    'modals.add' (val) {
      if (!val) {
        EventBus.$emit('initUserFormData')
      }
    },
    'modals.edit' (val) {
      if (!val) {
        EventBus.$emit('initUserFormData')
      }
    }
  },
  mounted () {
    this.loadUsersList(this.initialPage, null)
    this.$windowOpenerHelper.listenToPopUpMessage('reload_users_table', this.loadUsersList)
  },
  methods: {
    async loadUsersList (page = 1, itemPerPage = null, backToPageOne = false) {
      this.$vs.loading({container: '#usersDataContainer'})
      if (itemPerPage !== null) {
        this.itemPerPage = itemPerPage
      } else if (this.itemPerPage !== null) {
        itemPerPage = this.itemPerPage
      }
      this.$router.replace({ query: { ...this.usersSearchParams, page, itemPerPage } })
      const response = await User.all({ ...this.usersSearchParams, page, itemPerPage })
      this.users = response.data.data.users
      this.userPages = response.data.meta.pagination.last_page
      this.departmentOptions = response.data.meta.options.departments
      this.extensionOptions = response.data.meta.options.extensions
      this.unusedExtensionOptions = response.data.meta.options.unusedExtensions
      this.itemPerPage = response.data.meta.pagination.per_page
      if (backToPageOne === true) {
        this.initialPage = 1
      } else {
        this.initialPage = page
      }

      this.$vs.loading.close('#usersDataContainer > .con-vs-loading')
    },
    initRegister () {
      EventBus.$emit('registerUser')
    },
    registerSuccess () {
      this.loadUsersList()
      this.modals.add = false
    },
    resetUserForm () {
      EventBus.$emit('initUserFormData')
      EventBus.$emit('resetTab')
    },
    async inlineEditUser (key) {
      try {
        this.users[key].edit = true
        const editElem = this.users[key]

        const editObj = {
          context: editElem.context,
          landline: editElem.landline,
          mobile: editElem.mobile,
          name: editElem.name,
          username: editElem.username,
          extension_id: editElem.extension_id,
          department_id: editElem.department_id,
          new_password: editElem.new_password
        }
        this.usersEdit[key] = editObj
      } catch (e) {
        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: 'User not found',
          confirmButton: this.$t('confirm')
        })
      }
    },
    async inlineEditUserCancel (key) {
      try {
        this.users[key].context = this.usersEdit[key].context
        this.users[key].landline = this.usersEdit[key].landline
        this.users[key].mobile = this.usersEdit[key].mobile
        this.users[key].name = this.usersEdit[key].name
        this.users[key].username = this.usersEdit[key].username
        this.users[key].extension_id = this.usersEdit[key].extension_id
        this.users[key].department_id = this.usersEdit[key].department_id
        this.users[key].new_password = this.usersEdit[key].new_password
        this.users[key].edit = false
        delete this.usersEdit[key]
      } catch (e) {
        this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: 'User not found',
          confirmButton: this.$t('confirm')
        })
      }
    },
    async inlineUpdateUser (key, id) {
      try {
        const confirm = await this.$swalHelper.warning({
          title: this.$t('user_update_confirmation_title'),
          confirmButton: this.$t('user_update_confirmation_text'),
          cancelButton: this.$t('cancel')
        })

        if (confirm.value) {
          this.$vs.loading({background: 'primary', color: '#ffffff', container: `#updateUserButton${id}`, scale: 0.45})
          await User.update(this.users[key])
          this.users[key].edit = false
          this.users[key].password_display = this.users[key].new_password
          this.users[key].userFormErrors = {}
          this.$vs.loading.close(`#updateUserButton${id} > .con-vs-loading`)
          this.loadUsersList()
        }
      } catch (e) {
        const errors = this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
        this.users[key].userFormErrors = errors.validationErrors
        this.$vs.loading.close(`#updateUserButton${id} > .con-vs-loading`)
      }
    },
    async exportUser (e) {
      e.preventDefault()
      try {
        await User.export()
      } catch (e) {
        this.$alertHelper.error()
      }
    },
    async deleteUser (key, id) {
      const confirm = await this.$swalHelper.warning({
        title: this.$t('are_you_sure_you_want_to_delete_the_user'),
        confirmButton: this.$t('execution'),
        cancelButton: this.$t('cancel')
      })

      if (confirm.value) {
        await User.destroy({ id })
        this.loadUsersList(1, this.itemPerPage)
      }
    },
    async badgeChangeStatus (key) {
      try {
        if (this.users[key].is_active === 1) {
          this.users[key].is_active = 0
        } else {
          this.users[key].is_active = 1
        }
        await User.updateStatus(this.users[key])
        this.loadUsersList(1, this.itemPerPage)
        this.users[key].userFormErrors = {}
      } catch (e) {
        const errors = this.$swalHelper.displayCatchError({
          title: 'Error!',
          errorObject: e,
          confirmButton: this.$t('confirm')
        })
        this.users[key].userFormErrors = errors.validationErrors
      }
    },
    addUserModal () {
      this.modals.add = true
    },
    addNewUserPopup () {
      Utils.openPopUp(this.$store.getters.ctiPopUp, 'add-user', {}, this.addUserModal)
    },
    async editUserPermissionModal (params) {
      EventBus.$emit('loadUserPermission', params.id)
      this.modals.permissions = true
    },
    updateUserPermission () {
      EventBus.$emit('initUpdateUserPermission')
    },
    updateUserPermissionSuccess (response) {
      this.$swalHelper.success({
        title: this.$t('action_success'),
        text: this.$t(response.data),
        confirmButton: this.$t('confirm')
      })
      this.modals.permissions = false
    },
    resetUserPermissionFormData () {
      EventBus.$emit('initUserPermissionFormData')
      EventBus.$emit('resetTab')
    },
    editUserPermissionPopup (key, id) {
      Utils.openPopUp(this.$store.getters.ctiPopUp, 'edit-user-permission', {id}, this.editUserPermissionModal)
    }
  }
}
</script>

<style>
.swal2-popup {
  width: 33em !important;
}
</style>
